import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  addItemToLocalCart,
  updateLocalCartQuantity,
  removeItemFromLocalCart,
} from "../../redux/locallCartSlice";
import "./index.css";
import { getAvailableDayNames, getCurrentDayName } from "../../utility/Utility";
import { Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { trackAddToCart } from "../../utility/analyticsUtility";

interface Props {
  id: string;
  tinyId?: number;
  skuId?: string;
  name: string;
  price: any;
  description: any;
  imageUrl: string;
  portionName?: string;
  additionalCost?: number;
  servingTime?: string;
  availableDays?: number[];
  supportsPreOrdering?: boolean;
  orderClosingTime?: string;
  maxDaysToScheduleAhead?: number;
  menuId?: string;
  disabled?: boolean;
  availableStock?: number;
  orderType?: "Delivery" | "Pickup" | null;
  productId?: string;
}

const QuantityChangeButton: React.FC<Props> = ({
  id,
  name,
  price,
  description,
  imageUrl,
  portionName,
  additionalCost,
  servingTime,
  availableDays = [],
  supportsPreOrdering,
  orderClosingTime = "",
  maxDaysToScheduleAhead = 0,
  menuId,
  tinyId,
  disabled = false,
  availableStock,
  orderType,
  productId,
  skuId
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);

  // Get scheduled order info from Redux
  const isScheduleOrder = useSelector((state: RootState) => state.orderType?.isScheduleOrder);
  const scheduledDate = useSelector((state: RootState) => state.orderType?.scheduledDate);

  const portionSize = portionName || "REGULAR";
  const uniqueId = `${id}-${portionSize}`;
  const finalAdditionalCost = additionalCost ?? 0;
  const finalPrice = price + finalAdditionalCost;
  const cartItem = useSelector((state: RootState) =>
    state.localCartList.localCartList.find((item) => item.id === uniqueId)
  );
  // console.log("Cart item for", uniqueId, ":", cartItem);
  
  // Check if quantity would exceed available stock
  const currentQuantity = cartItem?.quantity || 0;
  const wouldExceedStock = availableStock !== undefined && currentQuantity >= availableStock;
  
  // const incremntDisable =
  //   (description === "CustomMenu" || description === "Custom") &&
  //   (!cartItem || cartItem.quantity === 0 || cartItem.quantity == null);

  // Helper function to check if item is available for the order date
  const isItemAvailableForOrderDate = (availableDays: number[]): boolean => {
    if (!availableDays || availableDays.length === 0) return true;
    
    // Check against scheduled date if it's a scheduled order, otherwise check today
    let targetDate: Date;
    if (isScheduleOrder && scheduledDate) {
      targetDate = new Date(scheduledDate);
    } else {
      targetDate = new Date();
    }
    
    const dayOfWeek = targetDate.getDay();
    return availableDays.includes(dayOfWeek);
  };

  // Get the target date name for the modal
  const getTargetDateName = (): string => {
    if (isScheduleOrder && scheduledDate) {
      const date = new Date(scheduledDate);
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    }
    return getCurrentDayName();
  };

  const handleIncrement = () => {
    if (availableDays && !isItemAvailableForOrderDate(availableDays)) {
      setShowAvailabilityModal(true);
      return;
    }
    addOrUpdateItem();
  };

  const addOrUpdateItem = () => {
    if (cartItem) {
      // console.log("Updating quantity for", uniqueId, "to", cartItem.quantity + 1);
      dispatch(
        updateLocalCartQuantity({
          id: uniqueId,
          quantity: cartItem.quantity + 1,
        })
      );
      // Track add to cart with correct item details
      trackAddToCart(uniqueId, name, finalPrice, 1);
    } else {
      // console.log("Adding new item to cart:", uniqueId);
      dispatch(
        addItemToLocalCart({
          id: uniqueId,
          name,
          tinyId: tinyId,
          skuId: skuId,
          productId: productId,
          price: finalPrice,
          description,
          imageUrl,
          quantity: 1,
          customItems: [],
          portionSize,
          itemType: "",
          extraItems: [],
          servingTime: servingTime,
          supportsPreOrdering: supportsPreOrdering,
          availableDays: availableDays,
          orderClosingTime: orderClosingTime,
          maxDaysToScheduleAhead: maxDaysToScheduleAhead,
          menuId: menuId,
          ...(description === "CustomMenu" || description === "Custom" ? { customItemTotal: finalPrice } : {}),
        })
      );
      // Track add to cart for new item
      trackAddToCart(uniqueId, name, finalPrice, 1);
    }
  };

  const handleDecrement = () => {
    if (cartItem) {
      if (cartItem.quantity > 1) {
        dispatch(
          updateLocalCartQuantity({
            id: uniqueId,
            quantity: cartItem.quantity - 1,
          })
        );
      } else {
        dispatch(removeItemFromLocalCart({ id: uniqueId }));
      }
    }
  };

  const handleProceed = () => {
    setShowAvailabilityModal(false);
    addOrUpdateItem();
    navigate("/shoppingCart");
  };

  return (
    <>
      <div className="quantity-selector">
        <button
          className="decrement-btn"
          onClick={handleDecrement}
          disabled={!cartItem || disabled}
        >
          -
        </button>
        <span className="quantity">{cartItem ? cartItem.quantity : 0}</span>
        <button
          className="increment-btn"
          onClick={handleIncrement}
          disabled={ disabled || wouldExceedStock}
          title={wouldExceedStock ? `Maximum available: ${availableStock}` : ""}
        >
          +
        </button>
      </div>

      <Modal
        show={showAvailabilityModal}
        onHide={() => setShowAvailabilityModal(false)}
        centered
        backdrop="static"
        className="availability-modal"
      >
        <Modal.Header closeButton className="availability-modal-header">
          <Modal.Title>Item Not Available on Selected Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="availability-modal-message">
            {name} is not available on {getTargetDateName()}.<br />
            Available days: {getAvailableDayNames(availableDays)}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAvailabilityModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="availability-modal-proceed-btn"
            onClick={handleProceed}
          >
            Proceed Anyway
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QuantityChangeButton;